import React from 'react';
import { dayjs } from 'shared/utils/date';
import { useMutation } from 'hooks/apollo';
import { FormattedMessage, injectIntl } from 'react-intl';
import { DatePicker, Form, Row, Col } from 'antd';
import MultilineInput from 'components/MultilineInput';
import WebsiteSelect from 'components/WebsiteSelect';
import globalMessages from 'translations/messages';
import WebsiteLanguageSelect from 'components/WebsiteLanguageSelect';
import FormLabel from 'components/FormLabel';
import useGlobalContext from 'hooks/useGlobalContext';
import messages from './messages';
import mutation from './publishDistribution.gql';

const publishDistribution = ({
  intl: { formatMessage: $t },
  customerId, distribution, form, onDone,
}) => {
  const [save] = useMutation(mutation);
  const { message } = useGlobalContext();

  const onFinish = async (values) => {
    try {
      const { data: { published } } = await save({
        variables: {
          id: distribution.id,
          input: values,
        },
      });
      if (published.startDate && dayjs(published.startDate).isAfter()) {
        const publishDate = dayjs(published.startDate).fromNow();
        message.success($t(messages.publishSuccess, { publishDate }));
      } else {
        message.success($t(messages.publishSuccessNoDate));
      }
    } catch (error) {
      message.error($t(messages.publishError));
    }
    if (onDone) {
      onDone();
    }
  };

  const initialValues = {
    websiteId: distribution.website?.id,
    websiteLanguageId: distribution.websiteLanguageId,
    includedUrls: distribution.includedUrls,
    excludedUrls: distribution.excludedUrls,
    startDate: distribution.startDate ? dayjs(distribution.startDate) : null,
    endDate: distribution.endDate ? dayjs(distribution.endDate) : null,
  };

  return (
    <Form layout="vertical" form={form} onFinish={onFinish} initialValues={initialValues}>
      <Row gutter={16}>
        <Col md={12} sm={24}>
          <Form.Item name="startDate" label={<FormattedMessage {...messages.startDate} />}>
            <DatePicker style={{ width: '100%' }} allowClear showTime placeholder={$t(messages.never)} />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item name="endDate" label={<FormattedMessage {...messages.endDate} />}>
            <DatePicker style={{ width: '100%' }} allowClear showTime placeholder={$t(messages.never)} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col md={12} sm={24}>
          <Form.Item name="websiteId" label={<FormattedMessage {...globalMessages.website} />}>
            <WebsiteSelect customerId={customerId} />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.websiteId !== curValues.websiteId}>
            {({ getFieldValue }) => (
              <Form.Item name="websiteLanguageId" label={<FormattedMessage {...globalMessages.language} />}>
                <WebsiteLanguageSelect customerId={customerId} websiteId={getFieldValue('websiteId')} />
              </Form.Item>
            )}
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col md={12} sm={24}>
          <Form.Item name="includedUrls" label={<FormLabel label={messages.includedUrls} description={messages.includedUrlsDescription} />}>
            <MultilineInput placeholder={$t(messages.urlsPlaceholder)} />
          </Form.Item>
        </Col>
        <Col md={12} sm={24}>
          <Form.Item name="excludedUrls" label={<FormLabel label={messages.excludedUrls} description={messages.excludedUrlsDescription} />}>
            <MultilineInput placeholder={$t(messages.urlsPlaceholder)} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default injectIntl(publishDistribution);
